import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  type ForeignNationalInfoInput,
  useApplicationQuery,
  useApplicationStatusAction,
  useApprovalsQuery,
  useCorFieldUpdate,
  useFnmsRetry,
  useUpsertForeignNationalInfo,
  useUserQuery,
} from '@access-cms/api-client';
import { Alert, Spinner } from '@cmsgov/design-system';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { SYSTEM_ROLE, useRBAC } from '../../hooks/useRBAC';
import { useAuth } from '../../components/auth/AuthContext';
import AccessDeniedPage from '../../components/auth/AccessDeniedPage';
import { ApplicationInformationBanner } from '../../components/ApplicationBanner';
import Modal from '../../components/Modal/Modal';
import { Button, ButtonVariation } from '../../components/Button';
import { DigitalSignatureModal, type SignatureData } from '../../components/DigitalSignatureModal';
import { RequiredFieldsLegend } from '../../components/RequiredFieldsLegend';
import { formatSignatureName } from '../../utils/formatSignatureName';
import { buildCorSignaturePayload, type CorSignaturePayload } from './utils/cor-signature';
import { type ApprovalDetailsData, isAdditionalAccessSection, type PivCardSelection } from './types';
import { type ApplicationWithUser, mapApplicationToSections } from './applicationDataMapper';
import { SectionId } from './constants';
import {
  ApprovalDetailsAccordion,
  ApprovalDetailsActionsBar,
  ApprovalDetailsHeader,
  ApprovalDetailsHelp,
  ApprovalDetailsNavigation,
  ApprovalReturnFeedbackBanner,
} from './components';
import type { ApprovalEditState } from './components/ApprovalDetailsEditableRow';
import { useApprovalDetailsSequence } from './hooks';
import { buildReturnFieldIssues, ReturnFeedbackPayload } from './utils/returnFeedback';
import './ApprovalDetailsPage.css';

const APPROVAL_DETAILS_INSTRUCTION =
  'You must open and view every drawer before you may take an action on this application.';
const CLAIM_REQUIRED_ERROR_CODE = '403.EM.APPROVAL.4001';
const CLAIM_REQUIRED_MESSAGE =
  'You must claim this application before you can Approve, Return for Edits, or Decline and Delete it. To claim it, return to the Manage Approvals table, open the application, and select “Yes” in the pop-up.';

function getReviewDecisionErrorMessage(error: Error, fallbackMessage: string) {
  const errorCode =
    typeof error === 'object' && error !== null && 'code' in error ? (error as { code?: unknown }).code : undefined;

  return errorCode === CLAIM_REQUIRED_ERROR_CODE ? CLAIM_REQUIRED_MESSAGE : fallbackMessage;
}

function getApprovedBannerMessage(pageTitle: string) {
  return `${pageTitle} has been approved.`;
}

function getReturnedForEditsBannerMessage(pageTitle: string) {
  return `${pageTitle} has been returned for edits.`;
}

function getDeclinedDeletedBannerMessage(pageTitle: string) {
  return `${pageTitle} has been declined and deleted.`;
}

type PendingReviewNavigation = 'manage-approvals' | 'next' | 'previous' | null;

function getCredentialFields(pivCardSelection: PivCardSelection) {
  switch (pivCardSelection) {
    case 'external_federal_piv':
      return { credentialType: 'external_federal_piv' as const, physicalAccess: 'yes' as const };
    case 'piv':
      return { credentialType: 'piv' as const, physicalAccess: 'yes' as const };
    case 'remote_rla':
      return { credentialType: 'remote_rla' as const, physicalAccess: 'no' as const };
  }
}

function getApplicantNameParts(approvalDetailsData: ApprovalDetailsData) {
  const personalDetailsSection = approvalDetailsData.sections.find(
    (section) => section.id === SectionId.PERSONAL_DETAILS,
  );

  if (!personalDetailsSection) {
    return {
      firstName: '',
      lastName: '',
      middleName: '',
      suffix: '',
    };
  }

  const getRowValue = (rowId: string) => {
    const value =
      personalDetailsSection.rows
        .find((row) => row.id === rowId)
        ?.value?.toString()
        .trim() ?? '';

    return value === 'N/A' ? '' : value;
  };

  return {
    firstName: getRowValue('legal-first-name'),
    lastName: getRowValue('legal-last-name'),
    middleName: getRowValue('legal-middle-name'),
    suffix: getRowValue('suffix'),
  };
}

function ApprovalDetailsPage() {
  const baseTestId = 'approval-details-page';
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();

  // Confirm this application belongs to a review visible to the caller before
  // loading its details. This prevents a reviewer from bypassing the assigned
  // approvals queue by entering another application's URL directly.
  const assignedApprovalQuery = useApprovalsQuery({
    enabled: !!id,
    filters: { applicationId: id ?? '', limit: 1 },
  });
  const currentApproval = assignedApprovalQuery.data?.data?.find((approval) => approval.application_id === id);
  const hasVisibleApproval = currentApproval !== undefined;

  // ACP-2752: the FNMS visit id arrives asynchronously, after the submission reaches FNMS.
  // Without this the reviewer sits on a stale record — Approve disabled, the FNMS button
  // still offering to send — until they reload by hand. Declared before the query so the
  // interval can be read while it is being built.
  const [isAwaitingFnmsVisit, setIsAwaitingFnmsVisit] = useState(false);
  const [showFnmsSentBanner, setShowFnmsSentBanner] = useState(false);

  const applicationQuery = useApplicationQuery(id ?? '', {
    enabled: !!id && hasVisibleApproval,
    include: ['user', 'contract', 'laborCategory'],
    // Surface returnFeedback (reviewer/COR return comments) for returned
    // applications so the COR can read the required clarifications (ACP-1027).
    includeWorkflowProgress: true,
    // Only while a submission is actually in flight. A record that is not waiting on a
    // visit is never polled.
    refetchInterval: isAwaitingFnmsVisit ? 5000 : false,
  });
  const applicationData = applicationQuery.data;

  const approvalDetailsData = useMemo(() => {
    if (!applicationData) {
      return { sections: [] };
    }

    return {
      sections: mapApplicationToSections(applicationData as unknown as ApplicationWithUser),
    };
  }, [applicationData]);

  const additionalAccessData = approvalDetailsData.sections.find(isAdditionalAccessSection)?.additionalAccessData;

  const pageTitle = useMemo(() => {
    const { firstName, lastName, middleName, suffix } = getApplicantNameParts(approvalDetailsData);

    return [
      'Application -',
      [lastName, [firstName, middleName].filter(Boolean).join(' ')].filter(Boolean).join(', '),
      suffix,
    ]
      .filter(Boolean)
      .join(' ');
  }, [approvalDetailsData]);

  // ACP-247 (U3) / ACP-2226: FN approver actions leave details immediately after success
  // and return to Manage Approvals with a banner (avoids reloading a record the reviewer
  // may no longer be authorized to view). COR keeps the Next / Back-to-Table success modals.
  const { hasSystemRole, isAdmin } = useRBAC();
  const isForeignNationalApprover = hasSystemRole(SYSTEM_ROLE.FOREIGN_NATIONAL);

  const goToManageApprovals = useCallback(
    (successBannerMessage: string) => {
      navigate('/approvals', { state: { successBannerMessage } });
    },
    [navigate],
  );

  const approveMutation = useApplicationStatusAction({
    applicationId: id ?? '',
    onError: () => {
      setApprovalError('The application could not be approved. Please try again.');
    },
    onSuccess: () => {
      setApprovalError('');
      setIsApproveConfirmModalOpen(false);
      if (isForeignNationalApprover) {
        goToManageApprovals(getApprovedBannerMessage(pageTitle));

        return;
      }
      setIsApprovedModalOpen(true);
    },
  });

  const returnForEditsMutation = useApplicationStatusAction({
    applicationId: id ?? '',
    onError: (error) => {
      setReturnForEditsError(
        getReviewDecisionErrorMessage(error, 'The application could not be returned for edits. Please try again.'),
      );
    },
    onSuccess: () => {
      setEditStateByRowId({});
      setReturnNotes('');
      setReturnForEditsError('');
      setIsReturnForEditsModalOpen(false);
      if (isForeignNationalApprover) {
        goToManageApprovals(getReturnedForEditsBannerMessage(pageTitle));

        return;
      }
      setIsReturnedForEditsModalOpen(true);
    },
  });

  const declineDeleteMutation = useApplicationStatusAction({
    applicationId: id ?? '',
    onError: (error) => {
      setDeclineDeleteError(
        getReviewDecisionErrorMessage(error, 'The application could not be declined and deleted. Please try again.'),
      );
    },
    onSuccess: () => {
      setDeclineDeleteReason('');
      setDeclineDeleteError('');
      setIsDeclineDeleteModalOpen(false);
      if (isForeignNationalApprover) {
        goToManageApprovals(getDeclinedDeletedBannerMessage(pageTitle));

        return;
      }
      setIsDeclinedDeletedModalOpen(true);
    },
  });

  const corFieldUpdate = useCorFieldUpdate(id ?? '');

  // A Foreign National application is exactly one that carries the FN Information
  // section (the data mapper includes it only for non-US-citizen applicants). Its four
  // answers must be persisted before COR approve, or the server-side completeness gate
  // (400.EM.FN.4001) rejects the transition.
  const isForeignNationalApplication = useMemo(
    () => approvalDetailsData.sections.some((section) => section.id === SectionId.FOREIGN_NATIONAL_INFORMATION),
    [approvalDetailsData.sections],
  );
  const [foreignNationalValues, setForeignNationalValues] = useState<ForeignNationalInfoInput | null>(null);
  const handleForeignNationalValuesChange = useCallback((values: ForeignNationalInfoInput) => {
    setForeignNationalValues(values);
  }, []);
  const foreignNationalInfoUpsert = useUpsertForeignNationalInfo({ applicationId: id ?? '' });

  // ACP-247 (U3): the reviewer-facing action set is role-scoped. The FN approver working
  // an FNMS-stage Foreign National record gets the read-only FN drawer and the Send to
  // FNMS action; the COR gets the editable FN drawer and the COR decision set. Server-side
  // authorization is the source of truth; this only shapes the UX.
  const isFnmsStage = applicationData?.applicationStatus === 'FNMS_REVIEW';
  // ACP-2421: the submission is spent and the job is dead-lettered. The same action slot
  // becomes Retry, which replays that job — a fresh send from here would bypass the replay
  // and leave it orphaned, so the two are mutually exclusive by construction.
  const isFnmsSubmissionFailed = applicationData?.applicationStatus === 'FNMS_SUBMISSION_FAILED';
  // The retry endpoint authorizes ADMINISTRATOR alongside the FN approver, so the action has
  // to be reachable by both. Send to FNMS stays FN-only: that one is a review decision, while
  // the retry is unblocking an integration failure — which is exactly the kind of thing an
  // administrator is called in for, and the FN approver may not even be available for.
  const canRetryFnms = isForeignNationalApprover || isAdmin;
  const showSendToFnms =
    isForeignNationalApplication &&
    ((isFnmsStage && isForeignNationalApprover) || (isFnmsSubmissionFailed && canRetryFnms));
  const isPermanentResident =
    (applicationData?.applicationData as { citizenshipDetails?: { isPermanentResident?: boolean } } | null | undefined)
      ?.citizenshipDetails?.isPermanentResident === true;
  // Hide additional access sections from the FN approver's view.
  const visibleSections = isForeignNationalApprover
    ? approvalDetailsData.sections.filter((section) => !isAdditionalAccessSection(section))
    : approvalDetailsData.sections;
  // fnmsVisitId is present once an optional FNMS submission succeeds. Non-permanent residents
  // still need that visit before approval; Permanent Residents do not. (Cast: not yet in the
  // generated api-types — the server exposes it on the application detail response.)
  const fnmsVisitId = (applicationData as { fnmsVisitId?: number | null } | undefined)?.fnmsVisitId;
  const hasBeenSentToFnms = typeof fnmsVisitId === 'number' && fnmsVisitId > 0;

  // ACP-2752 AC3: the visit is the thing we were waiting for, so arriving ends the polling.
  // A record that is not awaiting a visit is never polled.
  useEffect(() => {
    if (hasBeenSentToFnms) setIsAwaitingFnmsVisit(false);
  }, [hasBeenSentToFnms]);
  const [isSendToFnmsModalOpen, setIsSendToFnmsModalOpen] = useState(false);
  const sendToFnmsMutation = useApplicationStatusAction({
    applicationId: id ?? '',
    onError: () => {
      setApprovalError('The Foreign National visit could not be created. Please try again.');
    },
    onSuccess: () => {
      setApprovalError('');
      setIsSendToFnmsModalOpen(false);
      goToManageApprovals(`${pageTitle} was sent to FNMS. The visit is being created.`);
    },
  });
  // ACP-2421: unlike the original send, a retry keeps the reviewer on the record. The
  // application returns to FNMS_REVIEW and they have reason to watch it from here, so this
  // refetches in place instead of navigating away.
  const fnmsRetryMutation = useFnmsRetry({
    applicationId: id ?? '',
    onError: () => {
      setApprovalError('The FNMS submission could not be retried. Please try again.');
    },
    onSuccess: () => {
      setApprovalError('');
      setIsSendToFnmsModalOpen(false);
      // ACP-2752: the submission is on its way but FNMS has not answered yet. Say so, and
      // start watching for the visit so the record updates itself when it lands.
      setShowFnmsSentBanner(true);
      setIsAwaitingFnmsVisit(true);
      void applicationQuery.refetch();
    },
  });

  const signerQuery = useUserQuery(user?.id ?? '', { enabled: !!user?.id });
  const signerFullName = useMemo(() => {
    return formatSignatureName({
      firstName: signerQuery.data?.firstName,
      lastName: signerQuery.data?.lastName,
      middleName: signerQuery.data?.middleName,
      suffix: signerQuery.data?.suffix,
    });
  }, [signerQuery.data?.firstName, signerQuery.data?.lastName, signerQuery.data?.middleName, signerQuery.data?.suffix]);
  const { nextApproval, previousApproval } = useApprovalDetailsSequence();
  const [reviewedSectionIds, setReviewedSectionIds] = useState<string[]>([]);
  const [editStateByRowId, setEditStateByRowId] = useState<Record<string, ApprovalEditState>>({});
  const [returnNotes, setReturnNotes] = useState('');
  const [returnForEditsError, setReturnForEditsError] = useState('');
  const [declineDeleteReason, setDeclineDeleteReason] = useState('');
  const [declineDeleteError, setDeclineDeleteError] = useState('');
  const [hasFormSectionPendingChanges, setHasFormSectionPendingChanges] = useState(false);
  const [workingSixMonths, setWorkingSixMonths] = useState<boolean | null>(null);
  const [pivCardSelection, setPivCardSelection] = useState<PivCardSelection | null>(null);
  const [isApproveConfirmModalOpen, setIsApproveConfirmModalOpen] = useState(false);
  const [approvalError, setApprovalError] = useState('');
  const [isApproveSignatureModalOpen, setIsApproveSignatureModalOpen] = useState(false);
  // ACP-891: adopted COR signature, captured for approval decisions.
  const [corSignature, setCorSignature] = useState<CorSignaturePayload | null>(null);
  const [isSigningInProgress, setIsSigningInProgress] = useState(false);
  const [signatureError, setSignatureError] = useState('');
  const [isApprovedModalOpen, setIsApprovedModalOpen] = useState(false);
  const [isDeclineDeleteModalOpen, setIsDeclineDeleteModalOpen] = useState(false);
  const [isDeclinedDeletedModalOpen, setIsDeclinedDeletedModalOpen] = useState(false);
  const [isRevertModalOpen, setIsRevertModalOpen] = useState(false);
  const [isLeaveReviewModalOpen, setIsLeaveReviewModalOpen] = useState(false);
  const [pendingReviewNavigation, setPendingReviewNavigation] = useState<PendingReviewNavigation>(null);
  const [isReturnForEditsModalOpen, setIsReturnForEditsModalOpen] = useState(false);
  const [isReturnedForEditsModalOpen, setIsReturnedForEditsModalOpen] = useState(false);
  const [successBannerMessage, setSuccessBannerMessage] = useState<string | null>(
    () => (location.state as { successBannerMessage?: string } | null)?.successBannerMessage ?? null,
  );
  const [formResetKey, setFormResetKey] = useState(0);
  const hasRenderableSections = visibleSections.length > 0;
  const allSectionsReviewed = hasRenderableSections && reviewedSectionIds.length === visibleSections.length;
  const hasSavedRequestedEdits = useMemo(() => {
    return Object.values(editStateByRowId).some((editState) => editState.mode === 'saved');
  }, [editStateByRowId]);
  const hasPendingReviewWork = useMemo(() => {
    return Object.keys(editStateByRowId).length > 0 || hasFormSectionPendingChanges;
  }, [editStateByRowId, hasFormSectionPendingChanges]);

  useEffect(() => {
    setReviewedSectionIds([]);
    setEditStateByRowId({});
    setReturnForEditsError('');
    setDeclineDeleteReason('');
    setDeclineDeleteError('');
    setHasFormSectionPendingChanges(false);
    setWorkingSixMonths(null);
    setPivCardSelection(null);
    setApprovalError('');
    setIsApproveConfirmModalOpen(false);
    setIsApproveSignatureModalOpen(false);
    setIsApprovedModalOpen(false);
    setIsDeclineDeleteModalOpen(false);
    setIsDeclinedDeletedModalOpen(false);
    setIsLeaveReviewModalOpen(false);
    setPendingReviewNavigation(null);
    setIsRevertModalOpen(false);
    setIsReturnForEditsModalOpen(false);
    setIsReturnedForEditsModalOpen(false);
    setFormResetKey(0);
    window.scrollTo({ behavior: 'smooth', top: 0 });
  }, [id]);

  useEffect(() => {
    const nextSuccessBannerMessage =
      (location.state as { successBannerMessage?: string } | null)?.successBannerMessage ?? null;

    if (!nextSuccessBannerMessage) {
      return;
    }

    setSuccessBannerMessage(nextSuccessBannerMessage);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);
  const handleReviewedSectionsChange = useCallback((nextReviewedSectionIds: string[]) => {
    setReviewedSectionIds((previousReviewedSectionIds) => {
      const hasSameLength = previousReviewedSectionIds.length === nextReviewedSectionIds.length;
      const hasSameValues =
        hasSameLength &&
        previousReviewedSectionIds.every(
          (reviewedSectionId, index) => reviewedSectionId === nextReviewedSectionIds[index],
        );

      return hasSameValues ? previousReviewedSectionIds : nextReviewedSectionIds;
    });
  }, []);
  const handleRequestEdit = useCallback((rowId: string) => {
    setEditStateByRowId((prev) => {
      const current = prev[rowId];

      return {
        ...prev,
        [rowId]: {
          draftReason: current?.savedReason ?? '',
          mode: 'editing',
          savedReason: current?.savedReason ?? '',
        },
      };
    });
  }, []);
  const handleReasonChange = useCallback((rowId: string, value: string) => {
    setEditStateByRowId((prev) => {
      const current = prev[rowId] ?? { draftReason: '', mode: 'editing' as const, savedReason: '' };

      return {
        ...prev,
        [rowId]: {
          ...current,
          draftReason: value,
          mode: 'editing',
        },
      };
    });
  }, []);
  const handleSave = useCallback((rowId: string) => {
    setEditStateByRowId((prev) => {
      const current = prev[rowId];
      const nextReason = current?.draftReason.trim() ?? '';

      if (!current || !nextReason) {
        return prev;
      }

      return {
        ...prev,
        [rowId]: {
          draftReason: nextReason,
          mode: 'saved',
          savedReason: nextReason,
        },
      };
    });
  }, []);
  const handleCancel = useCallback((rowId: string) => {
    setEditStateByRowId((prev) => {
      const current = prev[rowId];

      if (!current) {
        return prev;
      }

      if (current.savedReason.trim().length > 0) {
        return {
          ...prev,
          [rowId]: {
            draftReason: current.savedReason,
            mode: 'saved',
            savedReason: current.savedReason,
          },
        };
      }

      const next = { ...prev };
      delete next[rowId];

      return next;
    });
  }, []);
  const handleEdit = useCallback((rowId: string) => {
    setEditStateByRowId((prev) => {
      const current = prev[rowId];

      if (!current) {
        return prev;
      }

      return {
        ...prev,
        [rowId]: {
          draftReason: current.savedReason,
          mode: 'editing',
          savedReason: current.savedReason,
        },
      };
    });
  }, []);
  const handleRemove = useCallback((rowId: string) => {
    setEditStateByRowId((prev) => {
      if (!prev[rowId]) {
        return prev;
      }

      const next = { ...prev };
      delete next[rowId];

      return next;
    });
  }, []);
  const handleOpenRevertModal = useCallback(() => {
    setIsRevertModalOpen(true);
  }, []);
  const handleCloseRevertModal = useCallback(() => {
    setIsRevertModalOpen(false);
  }, []);
  const handleConfirmRevert = useCallback(() => {
    setEditStateByRowId({});
    setWorkingSixMonths(null);
    setPivCardSelection(null);
    // ACP-247 (Vairav): "Clear All" is a full reset of the COR's approval view — it also
    // clears the four Foreign National answers, not just the requested edits. The
    // formResetKey bump remounts the FN form (blanking the fields); clearing the lifted
    // values here ensures stale answers can't be persisted on a later approve.
    setForeignNationalValues(null);
    setIsRevertModalOpen(false);
    setFormResetKey((prev) => prev + 1);
  }, []);
  const handleNavigateToPreviousApproval = useCallback(() => {
    if (!previousApproval?.application_id) {
      return;
    }

    if (hasPendingReviewWork) {
      setPendingReviewNavigation('previous');
      setIsLeaveReviewModalOpen(true);

      return;
    }

    navigate(`/approvals/application/${previousApproval.application_id}`);
  }, [hasPendingReviewWork, navigate, previousApproval]);
  const handleNavigateToNextApproval = useCallback(() => {
    if (!nextApproval?.application_id) {
      return;
    }

    if (hasPendingReviewWork) {
      setPendingReviewNavigation('next');
      setIsLeaveReviewModalOpen(true);

      return;
    }

    navigate(`/approvals/application/${nextApproval.application_id}`);
  }, [hasPendingReviewWork, navigate, nextApproval]);
  const handleBackToManageApprovals = useCallback(() => {
    if (hasPendingReviewWork) {
      setPendingReviewNavigation('manage-approvals');
      setIsLeaveReviewModalOpen(true);

      return;
    }

    navigate('/approvals');
  }, [hasPendingReviewWork, navigate]);
  const handleCloseLeaveReviewModal = useCallback(() => {
    setIsLeaveReviewModalOpen(false);
    setPendingReviewNavigation(null);
  }, []);
  const handleConfirmLeaveReview = useCallback(() => {
    if (pendingReviewNavigation === 'manage-approvals') {
      navigate('/approvals');
    } else if (pendingReviewNavigation === 'next' && nextApproval?.application_id) {
      navigate(`/approvals/application/${nextApproval.application_id}`);
    } else if (pendingReviewNavigation === 'previous' && previousApproval?.application_id) {
      navigate(`/approvals/application/${previousApproval.application_id}`);
    }

    setIsLeaveReviewModalOpen(false);
    setPendingReviewNavigation(null);
  }, [navigate, nextApproval, pendingReviewNavigation, previousApproval]);
  const handleOpenReturnForEditsModal = useCallback(() => {
    setReturnForEditsError('');
    setIsReturnForEditsModalOpen(true);
  }, []);
  const handleCloseReturnForEditsModal = useCallback(() => {
    if (returnForEditsMutation.isPending) return;
    setReturnNotes('');
    setReturnForEditsError('');
    setIsReturnForEditsModalOpen(false);
  }, [returnForEditsMutation.isPending]);
  const handleConfirmReturnForEdits = useCallback(() => {
    if (!returnNotes.trim()) return;

    setReturnForEditsError('');

    const fieldIssues = buildReturnFieldIssues(approvalDetailsData.sections, editStateByRowId);
    const returnFeedback: ReturnFeedbackPayload = {
      fieldIssues,
      generalNotes: returnNotes.trim(),
      returnedAt: new Date().toISOString(),
      returnedBy: {
        email: user?.email ?? '',
        first_name: signerQuery.data?.firstName ?? '',
        id: user?.id ?? '',
        last_name: signerQuery.data?.lastName ?? '',
      },
    };

    returnForEditsMutation.mutate({
      action: 'RETURN',
      notes: returnNotes.trim(),
      returnFeedback,
    });
  }, [returnNotes, approvalDetailsData, editStateByRowId, user, signerQuery.data, returnForEditsMutation]);
  const handleOpenApproveSignatureModal = useCallback(() => {
    setApprovalError('');
    // ACP-891 is a COR digital signature: only the COR signs their review decision. The FN
    // approver adjudicates the FNMS stage and does not sign, so skip the signature step and go
    // straight to the approve confirmation (their APPROVE carries no corSignature).
    if (isForeignNationalApprover) {
      setIsApproveConfirmModalOpen(true);

      return;
    }
    setIsApproveSignatureModalOpen(true);
  }, [isForeignNationalApprover]);

  const handleWorkingSixMonthsChange = useCallback((value: boolean) => {
    setWorkingSixMonths(value);
  }, []);
  const handlePivCardSelectionChange = useCallback((value: PivCardSelection) => {
    setPivCardSelection(value);
  }, []);
  const handleCloseApproveSignatureModal = useCallback(() => {
    setIsApproveSignatureModalOpen(false);
  }, []);
  const handleApproveSignatureSubmit = useCallback(
    async (signatureData: SignatureData) => {
      if (!id) return;
      setSignatureError('');
      setIsSigningInProgress(true);
      try {
        // Drawn signatures upload the PNG to S3 here (may take a moment while the
        // malware scan completes); styled signatures resolve immediately.
        const payload = await buildCorSignaturePayload(id, signatureData, applicationData, 'application');
        setCorSignature(payload);
        setIsApproveSignatureModalOpen(false);
        setIsApproveConfirmModalOpen(true);
      } catch (error) {
        setSignatureError(
          error instanceof Error ? error.message : 'Could not process your signature. Please try again.',
        );
      } finally {
        setIsSigningInProgress(false);
      }
    },
    [id, applicationData],
  );
  const handleCloseApproveConfirmModal = useCallback(() => {
    if (corFieldUpdate.isPending || foreignNationalInfoUpsert.isPending || approveMutation.isPending) {
      return;
    }

    setApprovalError('');
    setIsApproveConfirmModalOpen(false);
  }, [approveMutation.isPending, corFieldUpdate.isPending, foreignNationalInfoUpsert.isPending]);

  const handleConfirmApprove = useCallback(async () => {
    if (!id) return;
    setApprovalError('');

    // Every COR-authored field is already locked by the FNMS stage, so this APPROVE carries no writes.
    if (isForeignNationalApprover) {
      approveMutation.mutate({ action: 'APPROVE', corSignature: corSignature ?? undefined });

      return;
    }

    // COR review stage: both answers are required before approving. The reviewer's in-session
    // selection wins over whatever was already persisted on the application.
    const seasonal = workingSixMonths ?? additionalAccessData?.workingSixMonths ?? null;
    const credential = pivCardSelection ?? additionalAccessData?.pivCardSelection ?? null;

    if (seasonal === null || credential === null) {
      return;
    }

    // Persist the COR's Foreign National Information answers before approving, so the
    // server-side completeness gate sees the four answers in the same journey.
    if (isForeignNationalApplication && foreignNationalValues) {
      try {
        await foreignNationalInfoUpsert.mutateAsync({
          body: {
            cmsOrganization: foreignNationalValues.cmsOrganization ?? '',
            informationAccess: foreignNationalValues.informationAccess ?? '',
            informationSystems: foreignNationalValues.informationSystems ?? '',
            logicalAccess: foreignNationalValues.logicalAccess ?? '',
          },
        });
      } catch {
        setApprovalError('The Foreign National Information could not be saved. Please try again.');

        return;
      }
    }

    try {
      if (seasonal !== additionalAccessData?.workingSixMonths) {
        await corFieldUpdate.mutateAsync({ body: { contractDetails: { seasonal } } });
      }

      // The credential decision is made once, at the first COR approval. On a re-approval
      // after a VAS return the application already carries corApproval, so the fields are
      // omitted — keyed off the persisted decision, not the status, because a resubmitted
      // application re-enters COR_REVIEW.
      const body = additionalAccessData?.persistedPivCardSelection
        ? { action: 'APPROVE' as const, corSignature: corSignature ?? undefined }
        : {
            action: 'APPROVE' as const,
            corSignature: corSignature ?? undefined,
            ...getCredentialFields(credential),
          };
      approveMutation.mutate(body);
    } catch {
      setApprovalError('The additional access information could not be saved. Please try again.');
    }
  }, [
    additionalAccessData?.persistedPivCardSelection,
    additionalAccessData?.pivCardSelection,
    additionalAccessData?.workingSixMonths,
    approveMutation,
    corFieldUpdate,
    corSignature,
    foreignNationalInfoUpsert,
    foreignNationalValues,
    id,
    isForeignNationalApplication,
    isForeignNationalApprover,
    pivCardSelection,
    workingSixMonths,
  ]);
  const handleOpenDeclineDeleteModal = useCallback(() => {
    setDeclineDeleteError('');
    setIsDeclineDeleteModalOpen(true);
  }, []);
  const handleCloseDeclineDeleteModal = useCallback(() => {
    if (declineDeleteMutation.isPending) return;
    setDeclineDeleteReason('');
    setDeclineDeleteError('');
    setIsDeclineDeleteModalOpen(false);
  }, [declineDeleteMutation.isPending]);
  const handleConfirmDeclineDelete = useCallback(() => {
    if (!declineDeleteReason.trim()) return;

    setDeclineDeleteError('');

    // FN approver at the FNMS stage issues the dedicated DECLINE decision (→ REJECTED);
    // the COR path keeps the existing REJECT. Both land in the same terminal state.
    declineDeleteMutation.mutate({
      action: showSendToFnms ? 'DECLINE' : 'REJECT',
      notes: declineDeleteReason.trim(),
    });
  }, [declineDeleteReason, declineDeleteMutation, showSendToFnms]);
  const handleBackToTable = useCallback(() => {
    const successMessage = isApprovedModalOpen
      ? getApprovedBannerMessage(pageTitle)
      : isReturnedForEditsModalOpen
      ? getReturnedForEditsBannerMessage(pageTitle)
      : isDeclinedDeletedModalOpen
      ? getDeclinedDeletedBannerMessage(pageTitle)
      : null;

    setIsApprovedModalOpen(false);
    setIsReturnedForEditsModalOpen(false);
    setIsDeclinedDeletedModalOpen(false);
    navigate('/approvals', {
      state: successMessage ? { successBannerMessage: successMessage } : null,
    });
  }, [isApprovedModalOpen, isDeclinedDeletedModalOpen, isReturnedForEditsModalOpen, navigate, pageTitle]);
  const navigateToNextApplication = useCallback(
    (bannerMessage?: string) => {
      if (!nextApproval?.application_id) {
        return;
      }

      setIsApprovedModalOpen(false);
      setIsReturnedForEditsModalOpen(false);
      setIsDeclinedDeletedModalOpen(false);
      navigate(`/approvals/application/${nextApproval.application_id}`, {
        state: bannerMessage ? { successBannerMessage: bannerMessage } : null,
      });
    },
    [navigate, nextApproval],
  );
  const handleNextApplication = useCallback(() => {
    navigateToNextApplication(isApprovedModalOpen ? getApprovedBannerMessage(pageTitle) : undefined);
  }, [isApprovedModalOpen, navigateToNextApplication, pageTitle]);
  const handleNextApplicationAfterDeclineDelete = useCallback(() => {
    navigateToNextApplication(getDeclinedDeletedBannerMessage(pageTitle));
  }, [navigateToNextApplication, pageTitle]);
  const handleNextApplicationAfterReturnForEdits = useCallback(() => {
    navigateToNextApplication(getReturnedForEditsBannerMessage(pageTitle));
  }, [navigateToNextApplication, pageTitle]);
  const isApprovalSubmitting =
    corFieldUpdate.isPending || foreignNationalInfoUpsert.isPending || approveMutation.isPending;
  const isClaimedByCurrentUser = !!user?.id && currentApproval?.assigned_to_user_id === user.id;
  const requiresFnmsClaim = isForeignNationalApprover && isFnmsStage && !isAdmin && !isClaimedByCurrentUser;
  // ACP-2421: a parked submission can only reach FNMS_REVIEW or FNMS_ERROR, so every review
  // decision is refused by the server from here. The gates below keyed off `isFnmsStage`,
  // which is false while parked — that short-circuits the FNMS checks off and would leave
  // Approve and Decline enabled, promising an outcome the record cannot reach. The retry is
  // the one action that can succeed, so it is the one action offered.
  //
  // Both conditions have to hold: ACP-2240 asks whether this reviewer may decide at all, and
  // this asks whether the record is in a state where any decision can land.
  const canSubmitReviewDecision = !requiresFnmsClaim && !isFnmsSubmissionFailed;
  const canDeclineAndDelete = allSectionsReviewed && canSubmitReviewDecision;
  // Permanent Residents may be approved directly from the FNMS review stage. Other foreign
  // national application types still require an external FNMS visit before approval.
  const canApprove =
    allSectionsReviewed &&
    canSubmitReviewDecision &&
    !hasSavedRequestedEdits &&
    (!isFnmsStage || isPermanentResident || hasBeenSentToFnms);
  const canReturnForEdits = allSectionsReviewed && canSubmitReviewDecision && hasSavedRequestedEdits;
  // ACP-247 (Vairav): "Clear All" resets both the requested edits AND the four Foreign
  // National answers, so it is enabled whenever there is something to reset — saved requested
  // edits OR unsaved changes to the FN answers (hasFormSectionPendingChanges is now dirty-
  // tracked). Unlike Approve/Return/Decline it does NOT require every section to be reviewed:
  // a reviewer mid-edit must be able to reset. It disables again once everything is back to
  // the saved baseline.
  const canRevert = hasSavedRequestedEdits || hasFormSectionPendingChanges;
  // Send to FNMS requires the reviewer to have opened every drawer, like the other decisions, and
  // is available only until the external visit exists. The submission happens once per journey,
  // so disabling it afterward prevents a confusing double-submit.
  const isFnmsActionPending = sendToFnmsMutation.isPending || fnmsRetryMutation.isPending;
  // The retry does not inherit the send's "every drawer opened" rule: the reviewer already
  // satisfied it to get here, and the record is parked on an integration failure rather than
  // awaiting their judgement. `hasBeenSentToFnms` is likewise irrelevant — a visit may well
  // exist from the attempt that failed.
  const canSendToFnms = isFnmsSubmissionFailed
    ? !isFnmsActionPending
    : allSectionsReviewed && !hasBeenSentToFnms && !isFnmsActionPending;

  const handleOpenSendToFnmsModal = useCallback(() => {
    setApprovalError('');
    setIsSendToFnmsModalOpen(true);
  }, []);
  const handleCloseSendToFnmsModal = useCallback(() => {
    if (isFnmsActionPending) return;
    setIsSendToFnmsModalOpen(false);
  }, [isFnmsActionPending]);
  const handleConfirmSendToFnms = useCallback(() => {
    if (isFnmsSubmissionFailed) {
      fnmsRetryMutation.mutate({});

      return;
    }
    sendToFnmsMutation.mutate({ action: 'SEND_TO_FNMS' });
  }, [fnmsRetryMutation, isFnmsSubmissionFailed, sendToFnmsMutation]);

  if (assignedApprovalQuery.isLoading || (hasVisibleApproval && applicationQuery.isLoading)) {
    return (
      <div
        className='approval-details-page'
        data-testid={baseTestId}>
        <Spinner aria-valuetext='Loading application...' />
      </div>
    );
  }

  if (!assignedApprovalQuery.isLoading && !assignedApprovalQuery.isError && !hasVisibleApproval) {
    return <AccessDeniedPage />;
  }

  if (assignedApprovalQuery.isError) {
    return (
      <div
        className='approval-details-page'
        data-testid={baseTestId}>
        <Alert variation='error'>
          <p className='ds-u-margin-bottom--0'>Failed to verify application access. Please try again.</p>
        </Alert>
      </div>
    );
  }

  if (applicationQuery.isError) {
    return (
      <div
        className='approval-details-page'
        data-testid={baseTestId}>
        <Alert variation='error'>
          <p className='ds-u-margin-bottom--0'>Failed to load application. Please try again.</p>
        </Alert>
      </div>
    );
  }

  if (!hasRenderableSections) {
    return (
      <div
        className='approval-details-page'
        data-testid={baseTestId}>
        <Alert variation='error'>
          <p className='ds-u-margin-bottom--0'>Application details are unavailable.</p>
        </Alert>
      </div>
    );
  }

  return (
    <div
      className='approval-details-page'
      data-testid={baseTestId}>
      <div
        className='ds-l-row'
        data-testid={`${baseTestId}-row`}>
        <article
          aria-labelledby='approval-details-heading'
          className='ds-content ds-l-col--12'
          data-testid={`${baseTestId}-article`}
          id='content'
          tabIndex={-1}>
          <div
            className='approval-details-page__content'
            data-testid={`${baseTestId}-content`}>
            <div className='approval-details-page__header-wrap'>
              {/* ACP-2752: the retry keeps the reviewer here, so the confirmation has to be
                  here too. It reports what was sent and what to watch for, not success —
                  FNMS answers a few seconds later. */}
              {showFnmsSentBanner && (
                <ApplicationInformationBanner
                  bannerId={`approval-fnms-sent-${id ?? 'unknown'}`}
                  data-testid='approval-details-fnms-sent-banner'
                  dismissalScope='mount'
                  heading='The application was sent to FNMS.'
                  onDismiss={() => setShowFnmsSentBanner(false)}>
                  <ul
                    className='ds-u-margin-bottom--0'
                    data-testid='approval-details-fnms-sent-banner-body'>
                    <li>
                      When the application has been saved in FNMS, the &ldquo;Approve&rdquo; and &ldquo;Decline and
                      Delete&rdquo; buttons will be enabled.
                    </li>
                    <li>
                      If the application is not saved in FNMS, the &ldquo;Retry Send to FNMS&rdquo; button will be
                      enabled.
                    </li>
                  </ul>
                </ApplicationInformationBanner>
              )}
              {requiresFnmsClaim && (
                <div className='approval-details-page__claim-required-banner'>
                  <ApplicationInformationBanner
                    bannerId={`approval-claim-required-${currentApproval?.id ?? id ?? 'unknown'}`}
                    data-testid='approval-details-claim-required-alert'
                    dismissible={false}
                    heading='Claim Required'
                    variation='warn'>
                    {CLAIM_REQUIRED_MESSAGE}
                  </ApplicationInformationBanner>
                </div>
              )}
              {successBannerMessage && (
                <Alert
                  className='approval-details-page__success-banner ds-u-margin-bottom--3'
                  data-testid={`${baseTestId}-success-banner`}
                  role='status'
                  variation='success'>
                  <p
                    className='ds-u-margin-bottom--0'
                    data-testid={`${baseTestId}-success-banner-message`}>
                    {successBannerMessage}
                  </p>
                </Alert>
              )}
              {/* An active VAS-return correction cycle: the backend surfaces the
                  VAS feedback while the app is VAS_RETURNED AND during the
                  re-review after resubmission (SUBMITTED / COR_REVIEW), so the
                  COR always sees why VAS returned it. */}
              <ApprovalReturnFeedbackBanner
                returnFeedback={
                  applicationData?.returnFeedback ?? applicationData?.workflowProgress?.returnFeedback ?? null
                }
              />
              <div className='approval-details-page__header-row'>
                <ApprovalDetailsHeader
                  headingId='approval-details-heading'
                  instructionText={APPROVAL_DETAILS_INSTRUCTION}
                  title={pageTitle}
                />
                <ApprovalDetailsNavigation
                  canGoNext={!!nextApproval}
                  canGoPrevious={!!previousApproval}
                  className='approval-details-page__navigation'
                  onNext={handleNavigateToNextApproval}
                  onPrevious={handleNavigateToPreviousApproval}
                />
              </div>
            </div>
            <div className='approval-details-page__accordion-wrap'>
              <ApprovalDetailsAccordion
                key={id ?? ''}
                applicationId={applicationData?.id ?? id ?? ''}
                editStateByRowId={editStateByRowId}
                foreignNationalReadOnly={isForeignNationalApprover}
                formResetKey={formResetKey}
                sections={visibleSections}
                onCancel={handleCancel}
                onEdit={handleEdit}
                onForeignNationalValuesChange={handleForeignNationalValuesChange}
                onFormPendingChangesChange={setHasFormSectionPendingChanges}
                onPivCardSelectionChange={handlePivCardSelectionChange}
                onReasonChange={handleReasonChange}
                onRemove={handleRemove}
                onRequestEdit={handleRequestEdit}
                onReviewedSectionsChange={handleReviewedSectionsChange}
                onSave={handleSave}
                onWorkingSixMonthsChange={handleWorkingSixMonthsChange}
              />
            </div>
            <div className='approval-details-page__actions-wrap'>
              <ApprovalDetailsActionsBar
                canApprove={canApprove}
                canDeclineAndDelete={canDeclineAndDelete}
                canReturnForEdits={canReturnForEdits}
                canRevert={canRevert}
                canSendToFnms={canSendToFnms}
                isFnmsRetry={isFnmsSubmissionFailed}
                showSendToFnms={showSendToFnms}
                onApprove={handleOpenApproveSignatureModal}
                onBackToManageApprovals={handleBackToManageApprovals}
                onDeclineAndDelete={handleOpenDeclineDeleteModal}
                onReturnForEdits={handleOpenReturnForEditsModal}
                onRevert={handleOpenRevertModal}
                onSendToFnms={handleOpenSendToFnmsModal}
              />
            </div>
            <ApprovalDetailsHelp
              isForeignNationalApplication={isForeignNationalApplication}
              isForeignNationalApprover={isForeignNationalApprover}
            />
          </div>
        </article>
      </div>
      <DigitalSignatureModal
        applicationTitle='application'
        data-testid='approval-details-approve-signature-modal'
        fullName={signerFullName}
        isOpen={isApproveSignatureModalOpen}
        isSubmitting={isSigningInProgress}
        submitError={signatureError}
        onAdoptAndSign={handleApproveSignatureSubmit}
        onClose={handleCloseApproveSignatureModal}
      />
      <Modal
        className='approval-details-page__action-modal'
        data-testid='approval-details-leave-review-modal'
        heading='Are you sure you want to leave?'
        isOpen={isLeaveReviewModalOpen}
        onClose={handleCloseLeaveReviewModal}>
        <p
          className='approval-details-page__action-modal-body'
          data-testid='approval-details-leave-review-modal-body'>
          Are you sure you want to leave reviewing{' '}
          <span className='approval-details-page__action-modal-message-title'>{pageTitle}&apos;s</span> application
          before completing your review? You will lose any work you have done so far.
        </p>
        <div
          className='approval-details-page__action-modal-actions'
          data-testid='approval-details-leave-review-modal-actions'>
          <Button
            bordered
            isAutoWidth
            className='approval-details-page__action-modal-button'
            data-testid='approval-details-leave-review-modal-cancel'
            type='button'
            variation={ButtonVariation.GHOST}
            onClick={handleCloseLeaveReviewModal}>
            Cancel
          </Button>
          <Button
            isAutoWidth
            className='approval-details-page__action-modal-button'
            data-testid='approval-details-leave-review-modal-confirm'
            type='button'
            variation={ButtonVariation.SOLID}
            onClick={handleConfirmLeaveReview}>
            Continue
          </Button>
        </div>
      </Modal>
      <Modal
        className='approval-details-page__action-modal'
        data-testid='approval-details-approve-confirm-modal'
        heading='Are you sure you want to approve this application?'
        isOpen={isApproveConfirmModalOpen}
        onClose={handleCloseApproveConfirmModal}>
        <p
          className='approval-details-page__action-modal-body'
          data-testid='approval-details-approve-confirm-modal-body'>
          After this application is approved, you will not be able to return and make updates.
        </p>
        {approvalError && (
          <Alert
            data-testid='approval-details-approve-confirm-modal-error'
            role='alert'
            variation='error'>
            <p className='ds-u-margin-bottom--0'>{approvalError}</p>
          </Alert>
        )}
        <div
          className='approval-details-page__action-modal-actions'
          data-testid='approval-details-approve-confirm-modal-actions'>
          <Button
            bordered
            isAutoWidth
            className='approval-details-page__action-modal-button'
            data-testid='approval-details-approve-confirm-modal-cancel'
            disabled={isApprovalSubmitting}
            type='button'
            variation={ButtonVariation.GHOST}
            onClick={handleCloseApproveConfirmModal}>
            Cancel
          </Button>
          <Button
            isAutoWidth
            className='approval-details-page__action-modal-button'
            data-testid='approval-details-approve-confirm-modal-confirm'
            disabled={isApprovalSubmitting}
            type='button'
            variation={ButtonVariation.SOLID}
            onClick={handleConfirmApprove}>
            Approve Application
          </Button>
        </div>
      </Modal>

      {/* ACP-247 (U3): Send to FNMS — "Create Foreign National Visit?" confirmation.
          ACP-2421: the same modal carries the retry, since it is the same action slot. */}
      <Modal
        className='approval-details-page__action-modal'
        data-testid='approval-details-send-to-fnms-modal'
        heading={isFnmsSubmissionFailed ? 'Retry the FNMS submission?' : 'Create Foreign National Visit?'}
        isOpen={isSendToFnmsModalOpen}
        onClose={handleCloseSendToFnmsModal}>
        <p
          className='approval-details-page__action-modal-body'
          data-testid='approval-details-send-to-fnms-modal-body'>
          {isFnmsSubmissionFailed
            ? 'The automatic attempts to reach FNMS were used up, so this application is waiting here instead of moving on. This sends it to FNMS again. Please allow this operation to complete before leaving the page.'
            : 'This will create the visit for this Foreign National in the FNMS system. Are you sure you wish to create this visit? Please allow this operation to complete before leaving the page.'}
        </p>
        {approvalError && (
          <Alert
            data-testid='approval-details-send-to-fnms-modal-error'
            role='alert'
            variation='error'>
            <p className='ds-u-margin-bottom--0'>{approvalError}</p>
          </Alert>
        )}
        <div
          className='approval-details-page__action-modal-actions'
          data-testid='approval-details-send-to-fnms-modal-actions'>
          <Button
            bordered
            isAutoWidth
            className='approval-details-page__action-modal-button'
            data-testid='approval-details-send-to-fnms-modal-cancel'
            disabled={isFnmsActionPending}
            type='button'
            variation={ButtonVariation.GHOST}
            onClick={handleCloseSendToFnmsModal}>
            Cancel
          </Button>
          <Button
            isAutoWidth
            className='approval-details-page__action-modal-button'
            data-testid='approval-details-send-to-fnms-modal-confirm'
            disabled={isFnmsActionPending}
            type='button'
            variation={ButtonVariation.SOLID}
            onClick={handleConfirmSendToFnms}>
            {isFnmsSubmissionFailed ? 'Retry Send to FNMS' : 'Create Visit / Send to FNMS'}
          </Button>
        </div>
      </Modal>
      <Modal
        className='approval-details-page__action-modal'
        data-testid='approval-details-decline-delete-modal'
        heading='Are you sure you want to decline and delete this application?'
        isOpen={isDeclineDeleteModalOpen}
        onClose={handleCloseDeclineDeleteModal}>
        <div
          className='approval-details-page__action-modal-copy'
          data-testid='approval-details-decline-delete-modal-copy'>
          <p
            className='approval-details-page__action-modal-body'
            data-testid='approval-details-decline-delete-modal-body'>
            After this is declined and deleted, you will no longer be able to access this application.
          </p>
          <RequiredFieldsLegend data-testid='approval-details-decline-delete-modal-required-fields' />
          <div
            className='approval-details-page__action-modal-form'
            data-testid='approval-details-decline-delete-modal-form'>
            <label
              className='approval-details-page__action-modal-label'
              data-testid='approval-details-decline-delete-modal-label'
              htmlFor='approval-details-decline-delete-reason'>
              Enter a reason for declining and deleting this application
              <span
                aria-label='required'
                className='approval-details-page__action-modal-required'>
                *
              </span>
            </label>
            <textarea
              required
              aria-label='Enter a reason for declining and deleting this application'
              aria-required='true'
              className='approval-details-page__action-modal-textarea'
              data-testid='approval-details-decline-delete-modal-textarea'
              id='approval-details-decline-delete-reason'
              rows={4}
              value={declineDeleteReason}
              onChange={(event) => setDeclineDeleteReason(event.target.value)}
            />
          </div>
        </div>
        {declineDeleteError && (
          <Alert
            data-testid='approval-details-decline-delete-modal-error'
            role='alert'
            variation='error'>
            <p className='ds-u-margin-bottom--0'>{declineDeleteError}</p>
          </Alert>
        )}
        <div
          className='approval-details-page__action-modal-actions'
          data-testid='approval-details-decline-delete-modal-actions'>
          <Button
            bordered
            isAutoWidth
            className='approval-details-page__action-modal-button'
            data-testid='approval-details-decline-delete-modal-cancel'
            disabled={declineDeleteMutation.isPending}
            type='button'
            variation={ButtonVariation.GHOST}
            onClick={handleCloseDeclineDeleteModal}>
            Cancel
          </Button>
          <Button
            isAutoWidth
            className='approval-details-page__action-modal-button'
            data-testid='approval-details-decline-delete-modal-confirm'
            disabled={!declineDeleteReason.trim() || declineDeleteMutation.isPending}
            isLoading={declineDeleteMutation.isPending}
            loadingText='Declining...'
            type='button'
            variation={ButtonVariation.SOLID}
            onClick={handleConfirmDeclineDelete}>
            Decline and Delete Application
          </Button>
        </div>
      </Modal>
      <Modal
        className='approval-details-page__action-modal'
        data-testid='approval-details-return-for-edits-modal'
        heading='Do you want to return the application for edits?'
        isOpen={isReturnForEditsModalOpen}
        onClose={handleCloseReturnForEditsModal}>
        <div
          className='approval-details-page__action-modal-copy'
          data-testid='approval-details-return-for-edits-modal-copy'>
          <p
            className='approval-details-page__action-modal-body'
            data-testid='approval-details-return-for-edits-modal-body'>
            Are you sure you want to send this application back to the applicant for edits?
          </p>
          <RequiredFieldsLegend data-testid='approval-details-return-for-edits-modal-required-fields' />
          <div
            className='approval-details-page__action-modal-form'
            data-testid='approval-details-return-for-edits-modal-form'>
            <label
              className='approval-details-page__action-modal-label'
              data-testid='approval-details-return-for-edits-modal-label'
              htmlFor='approval-details-return-for-edits-notes'>
              Enter a reason for returning this application
              <span
                aria-label='required'
                className='approval-details-page__action-modal-required'>
                *
              </span>
            </label>
            <textarea
              required
              aria-label='Enter a reason for returning this application'
              aria-required='true'
              className='approval-details-page__action-modal-textarea'
              data-testid='approval-details-return-for-edits-modal-textarea'
              id='approval-details-return-for-edits-notes'
              rows={4}
              value={returnNotes}
              onChange={(event) => setReturnNotes(event.target.value)}
            />
          </div>
        </div>
        {returnForEditsError && (
          <Alert
            data-testid='approval-details-return-for-edits-modal-error'
            role='alert'
            variation='error'>
            <p className='ds-u-margin-bottom--0'>{returnForEditsError}</p>
          </Alert>
        )}
        <div
          className='approval-details-page__action-modal-actions'
          data-testid='approval-details-return-for-edits-modal-actions'>
          <Button
            bordered
            isAutoWidth
            className='approval-details-page__action-modal-button'
            data-testid='approval-details-return-for-edits-modal-cancel'
            disabled={returnForEditsMutation.isPending}
            type='button'
            variation={ButtonVariation.GHOST}
            onClick={handleCloseReturnForEditsModal}>
            Cancel
          </Button>
          <Button
            isAutoWidth
            className='approval-details-page__action-modal-button'
            data-testid='approval-details-return-for-edits-modal-confirm'
            disabled={!returnNotes.trim() || returnForEditsMutation.isPending}
            isLoading={returnForEditsMutation.isPending}
            loadingText='Returning...'
            type='button'
            variation={ButtonVariation.SOLID}
            onClick={handleConfirmReturnForEdits}>
            Return for Edits
          </Button>
        </div>
      </Modal>
      <Modal
        className='approval-details-page__action-modal'
        data-testid='approval-details-revert-modal'
        heading='Do you want to clear all requested edits?'
        isOpen={isRevertModalOpen}
        onClose={handleCloseRevertModal}>
        <p
          className='approval-details-page__action-modal-body'
          data-testid='approval-details-revert-modal-body'>
          Are you sure you want to clear all requested edits for this application?
        </p>
        <div
          className='approval-details-page__action-modal-actions'
          data-testid='approval-details-revert-modal-actions'>
          <Button
            bordered
            isAutoWidth
            className='approval-details-page__action-modal-button'
            data-testid='approval-details-revert-modal-cancel'
            type='button'
            variation={ButtonVariation.GHOST}
            onClick={handleCloseRevertModal}>
            Cancel
          </Button>
          <Button
            isAutoWidth
            className='approval-details-page__action-modal-button'
            data-testid='approval-details-revert-modal-confirm'
            type='button'
            variation={ButtonVariation.SOLID}
            onClick={handleConfirmRevert}>
            Clear All
          </Button>
        </div>
      </Modal>
      <Modal
        preventDismiss
        className='approval-details-page__action-modal'
        data-testid='approval-details-declined-deleted-modal'
        heading='Application Declined and Deleted'
        isOpen={isDeclinedDeletedModalOpen}
        onClose={() => undefined}>
        <div
          className='approval-details-page__action-modal-copy'
          data-testid='approval-details-declined-deleted-modal-copy'>
          <p
            className='approval-details-page__action-modal-body'
            data-testid='approval-details-declined-deleted-modal-message'>
            <span className='approval-details-page__action-modal-message-title'>{pageTitle}</span> has been Declined and
            Deleted.
          </p>
          <p
            className='approval-details-page__action-modal-body'
            data-testid='approval-details-declined-deleted-modal-question'>
            Would you like to continue to review the next application? Or would you like to return to the Manage
            Approvals table?
          </p>
        </div>
        <div
          className='approval-details-page__action-modal-actions'
          data-testid='approval-details-declined-deleted-modal-actions'>
          <Button
            bordered
            isAutoWidth
            className='approval-details-page__action-modal-button'
            data-testid='approval-details-declined-deleted-modal-back-to-table'
            type='button'
            variation={ButtonVariation.GHOST}
            onClick={handleBackToTable}>
            Back to Table
          </Button>
          <Button
            isAutoWidth
            className='approval-details-page__action-modal-button'
            data-testid='approval-details-declined-deleted-modal-next-application'
            disabled={!nextApproval}
            type='button'
            variation={ButtonVariation.SOLID}
            onClick={handleNextApplicationAfterDeclineDelete}>
            Next Application
          </Button>
        </div>
      </Modal>
      <Modal
        preventDismiss
        className='approval-details-page__action-modal'
        data-testid='approval-details-approved-modal'
        heading='Application Approved'
        isOpen={isApprovedModalOpen}
        onClose={() => undefined}>
        <div
          className='approval-details-page__action-modal-copy'
          data-testid='approval-details-approved-modal-copy'>
          <p
            className='approval-details-page__action-modal-body'
            data-testid='approval-details-approved-modal-message'>
            <span className='approval-details-page__action-modal-message-title'>{pageTitle}</span> has been Approved.
          </p>
          <p
            className='approval-details-page__action-modal-body'
            data-testid='approval-details-approved-modal-question'>
            Would you like to continue to review the next application? Or would you like to return to the Manage
            Approvals table?
          </p>
        </div>
        <div
          className='approval-details-page__action-modal-actions'
          data-testid='approval-details-approved-modal-actions'>
          <Button
            bordered
            isAutoWidth
            className='approval-details-page__action-modal-button'
            data-testid='approval-details-approved-modal-back-to-table'
            type='button'
            variation={ButtonVariation.GHOST}
            onClick={handleBackToTable}>
            Back to Table
          </Button>
          <Button
            isAutoWidth
            className='approval-details-page__action-modal-button'
            data-testid='approval-details-approved-modal-next-application'
            disabled={!nextApproval}
            type='button'
            variation={ButtonVariation.SOLID}
            onClick={handleNextApplication}>
            Next Application
          </Button>
        </div>
      </Modal>
      <Modal
        preventDismiss
        className='approval-details-page__action-modal'
        data-testid='approval-details-returned-for-edits-modal'
        heading='Application Returned for Edits'
        isOpen={isReturnedForEditsModalOpen}
        onClose={() => undefined}>
        <div
          className='approval-details-page__action-modal-copy'
          data-testid='approval-details-returned-for-edits-modal-copy'>
          <p
            className='approval-details-page__action-modal-body'
            data-testid='approval-details-returned-for-edits-modal-message'>
            <span className='approval-details-page__action-modal-message-title'>{pageTitle}</span> has been sent back to
            the applicant for edits.
          </p>
          <p
            className='approval-details-page__action-modal-body'
            data-testid='approval-details-returned-for-edits-modal-question'>
            Would you like to continue to review the next application? Or would you like to return to the Manage
            Approvals table?
          </p>
        </div>
        <div
          className='approval-details-page__action-modal-actions'
          data-testid='approval-details-returned-for-edits-modal-actions'>
          <Button
            bordered
            isAutoWidth
            className='approval-details-page__action-modal-button'
            data-testid='approval-details-returned-for-edits-modal-back-to-table'
            type='button'
            variation={ButtonVariation.GHOST}
            onClick={handleBackToTable}>
            Back to Table
          </Button>
          <Button
            isAutoWidth
            className='approval-details-page__action-modal-button'
            data-testid='approval-details-returned-for-edits-modal-next-application'
            disabled={!nextApproval}
            type='button'
            variation={ButtonVariation.SOLID}
            onClick={handleNextApplicationAfterReturnForEdits}>
            Next Application
          </Button>
        </div>
      </Modal>
    </div>
  );
}

export default ApprovalDetailsPage;
